import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { User } from 'shared'
import { useAuth } from '../contexts/AuthContext'
import {
  Alert,
  Button,
  Card,
  Divider,
  Field,
  Input,
  PageHeader,
  Tab,
  Tabs,
  TabsList,
  TabsPanel,
} from '../components/ui'
import EmailPreferences from '../components/EmailPreferences'
import { api } from '../utils/api'
import { toErrorMessage } from '../lib/errors'

/** Mirrors the backend's shared rule; the server checks it again regardless. */
const MIN_PASSWORD_LENGTH = 8

export default function Profile() {
  const { t, i18n } = useTranslation()
  const { user, setSession, updateUser } = useAuth()

  const [name, setName] = useState(user?.name ?? '')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')

  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null)
  const [savingName, setSavingName] = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)

  const handleUpdateName = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!name.trim()) return

    setSavingName(true)
    setMessage(null)

    try {
      const response = await api.put<{ user: User }>('/api/auth/me', { name: name.trim() })
      // The name is shown in the sidebar and on the account menu, so the
      // session has to learn about it too — otherwise the page says one thing
      // and the rest of the app another until the next sign-in.
      updateUser(response.user)
      setName(response.user.name)
      setMessage({ tone: 'success', text: t('profile.updateSuccess') })
    } catch (error) {
      setMessage({ tone: 'error', text: toErrorMessage(error) ?? t('profile.updateError') })
    } finally {
      setSavingName(false)
    }
  }

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!currentPassword || !newPassword) return

    setSavingPassword(true)
    setMessage(null)

    try {
      const response = await api.put<{ token: string }>('/api/auth/me/password', {
        currentPassword,
        newPassword,
      })
      // Changing the password ends every session for this account, this one
      // included. The server hands back a fresh token so the person who made
      // the change is not the one thrown out by it.
      if (response.token && user) setSession(response.token, user)
      setCurrentPassword('')
      setNewPassword('')
      setMessage({ tone: 'success', text: t('profile.passwordChanged') })
    } catch (error) {
      setMessage({ tone: 'error', text: toErrorMessage(error) ?? t('profile.passwordError') })
    } finally {
      setSavingPassword(false)
    }
  }

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang)
    localStorage.setItem('language', lang)
  }

  const nameUnchanged = name.trim() === (user?.name ?? '')
  const passwordTooShort = newPassword.length > 0 && newPassword.length < MIN_PASSWORD_LENGTH

  return (
    <div>
      <PageHeader title={t('profile.title')} subtitle={t('profile.settingsDescription')} />

      {message && (
        <Alert variant={message.tone === 'success' ? 'success' : 'error'} className="mb-6">
          {message.text}
        </Alert>
      )}

      <Tabs defaultValue="account">
        <TabsList className="mb-6">
          <Tab value="account">{t('profile.accountDetails')}</Tab>
          <Tab value="preferences">{t('profile.preferences')}</Tab>
        </TabsList>

        <TabsPanel value="account">
          <Card className="space-y-8 p-6">
            <form onSubmit={handleUpdateName}>
              <h2 className="font-semibold text-gray-950 dark:text-white">{t('auth.name')}</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('profile.nameDescription')}
              </p>

              <div className="mt-4 sm:max-w-md">
                <Field label={t('auth.name')} required>
                  <Input
                    type="text"
                    name="name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    autoComplete="name"
                  />
                </Field>
              </div>

              <Button
                type="submit"
                className="mt-4"
                loading={savingName}
                disabled={!name.trim() || nameUnchanged}
              >
                {t('profile.updateName')}
              </Button>
            </form>

            <Divider />

            <div>
              <h2 className="font-semibold text-gray-950 dark:text-white">{t('auth.email')}</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('profile.emailCannotChange')}
              </p>

              <div className="mt-4 sm:max-w-md">
                <Field label={t('auth.email')}>
                  <Input type="email" name="email" value={user?.email ?? ''} disabled />
                </Field>
              </div>
            </div>

            <Divider />

            <form onSubmit={handleChangePassword}>
              <h2 className="font-semibold text-gray-950 dark:text-white">
                {t('profile.changePassword')}
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('profile.passwordDescription')}
              </p>
              {/* Said before the change, not after: being signed out of your
                  other devices is the point, but it is a surprise if nobody
                  mentioned it. */}
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('profile.passwordEndsSessions')}
              </p>

              <div className="mt-4 space-y-4 sm:max-w-md">
                <Field label={t('profile.currentPassword')} required>
                  <Input
                    type="password"
                    name="current-password"
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    autoComplete="current-password"
                  />
                </Field>

                <Field
                  label={t('profile.newPassword')}
                  required
                  hint={t('auth.passwordHint', { min: MIN_PASSWORD_LENGTH })}
                >
                  <Input
                    type="password"
                    name="new-password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    autoComplete="new-password"
                  />
                </Field>
              </div>

              <Button
                type="submit"
                className="mt-4"
                loading={savingPassword}
                disabled={!currentPassword || !newPassword || passwordTooShort}
              >
                {t('profile.updatePassword')}
              </Button>
            </form>
          </Card>
        </TabsPanel>

        <TabsPanel value="preferences">
          <Card className="space-y-8 p-6">
            <div>
              <h2 className="font-semibold text-gray-950 dark:text-white">
                {t('profile.language')}
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('profile.languageDescription')}
              </p>
              <div className="mt-4 flex gap-3">
                <Button
                  variant={i18n.language.startsWith('en') ? 'primary' : 'secondary'}
                  onClick={() => handleLanguageChange('en')}
                >
                  English
                </Button>
                <Button
                  variant={i18n.language.startsWith('it') ? 'primary' : 'secondary'}
                  onClick={() => handleLanguageChange('it')}
                >
                  Italiano
                </Button>
              </div>
            </div>

            <Divider />

            <EmailPreferences />

            <Divider />

            <div>
              <h2 className="font-semibold text-gray-950 dark:text-white">{t('profile.role')}</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('profile.roleDescription')}
              </p>
              <p className="mt-3 text-sm font-medium text-gray-900 dark:text-gray-50">
                {user ? t(`admin.role_${user.role}`) : '—'}
              </p>
            </div>
          </Card>
        </TabsPanel>
      </Tabs>
    </div>
  )
}
